import { makeAutoObservable } from 'mobx';
import { pickBy } from 'lodash';
import { i18n } from 'core/localization';
import { apiFetch } from 'framework/api';
import {
	OAuthValidationResponse,
	SaveAuthenticationPayload,
	validateAuthentication,
} from 'areas/administration/authentication/api';
import { OAuthConfig, OAuthNotice, OAuthValidationStep } from 'areas/administration/authentication/types';
import { SimpleFieldsStore } from 'areas/administration/authentication/components/simple-fields/simpleFieldsStore';
import { configFields, toConfig } from './utils';

export class OAuthValidationStore {
	step: OAuthValidationStep = null;
	notice: OAuthNotice = null;
	validatedConfig: OAuthConfig = null;
	codeExpired = false;
	validating = false;

	constructor(private fieldsStore: SimpleFieldsStore) {
		makeAutoObservable(this);
	}

	get changed() {
		if (!this.validatedConfig) return false;

		const current = toConfig(this.fieldsStore.model);
		return configFields.some((field) => current[field] != this.validatedConfig[field]);
	}

	get currentStep() {
		if (this.validatedConfig) {
			return this.changed ? OAuthValidationStep.Changed : OAuthValidationStep.Validated;
		}

		return this.step;
	}

	get currentNotice(): OAuthNotice {
		if (this.validatedConfig) {
			return {
				type: 'info',
				text: this.changed ? i18n('Valid authentication changed') : i18n('Valid authentication'),
			};
		}

		return this.notice;
	}

	get started() {
		return this.step != null || this.validatedConfig != null;
	}

	get canValidate() {
		return this.started && !this.validating && this.currentStep != OAuthValidationStep.Validated;
	}

	get blocksSave() {
		return this.currentStep == OAuthValidationStep.Changed;
	}

	showCreateNotice() {
		this.notice = { type: 'info', text: i18n('Oauth needs validation') };
	}

	start(codeExpired: boolean = false) {
		this.step = OAuthValidationStep.Start;
		this.codeExpired = codeExpired;
		this.notice = { type: 'info', text: i18n('Validate start message') };
	}

	applyState(data: OAuthValidationResponse) {
		if (data.hasRefreshToken) {
			this.setValidated(data);
		} else if (data.userCode) {
			if (data.userCodeExpires != null && new Date(data.userCodeExpires) < new Date()) {
				this.start(true);
			} else {
				this.setWaitingForUser(data);
			}
		} else {
			this.start();
		}
	}

	setValidated(data: OAuthValidationResponse) {
		// fields missing in the response (e.g. a secret the server doesn't send back) are taken as validated as is
		this.validatedConfig = toConfig({ ...this.fieldsStore.model, ...pickBy(data, (value) => value != null) });
		this.step = OAuthValidationStep.Validated;
	}

	setWaitingForUser(data: OAuthValidationResponse) {
		this.step = OAuthValidationStep.WaitingForUser;
		this.notice = {
			type: 'info',
			text: data.message ?? '',
			url: data.verificationURL,
			expires: data.userCodeExpires,
		};
	}

	validate = async (payload: SaveAuthenticationPayload) => {
		const step = this.currentStep;

		this.validating = true;
		try {
			const result = await apiFetch(validateAuthentication(payload));
			this.validatedConfig = null;

			if (result.data?.hasRefreshToken) {
				this.setValidated(result.data);
				return;
			}

			if (result.data?.userCode) {
				this.setWaitingForUser(result.data);
				return;
			}

			this.showFailure(step, result.message, result.details?.[0]);
		} finally {
			this.validating = false;
		}
	};

	showFailure(step: OAuthValidationStep, message: string, detail: string) {
		switch (step) {
			case OAuthValidationStep.Start:
				this.notice = { type: 'error', text: this.codeExpired ? detail : message };
				this.codeExpired = false;
				break;
			case OAuthValidationStep.WaitingForUser:
				// keeps the user code and the verification url on screen when there is nothing to replace them with
				if (detail) {
					this.notice = { type: 'info', text: detail };
				}
				break;
			default:
				this.step = OAuthValidationStep.Changed;
				this.notice = { type: 'error', text: message };
				break;
		}
	}
}
